import { CaretDownOutlined } from '@ant-design/icons';
import { Button, Collapse, Row, Col, Typography, Spin, Tooltip, Divider, Popover, Switch } from 'antd';
import { EditIcon, DeleteIcon, CopyIcon } from 'components/Icons';
import _ from 'lodash'
import React, { useCallback, useState, useEffect } from 'react'
import ModalUtil from 'utils/modalUtil';
import { BASE_GUTTER, SEVERITY_COLOR_TEXT } from 'constants/Layout';
import SLAForm from './SLAForm';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import moment from 'moment';
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import AuthorisedContainer from 'components/AuthorisedContainer';
import { PermissionSetMap } from 'constants/RBACConstants';
import classNames from 'classnames';
import DeletePopup from 'components/DeletePopup';
import message from 'components/CustomMessage';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import WarningPopup from 'components/WarningPopup';
import ACRiskRatingTag from 'ac-components/ac-risk-rating-tag/ACRiskRatingTag';
import { renderPopOverContent } from './FindingSLAConfigurationV3/SLAPolicyForm/utils';

const {Title, Text} = Typography

export default function FindingsSLAConfigurationV2() {
    const { globalFilterHash } = useGlobalFilterContext();
    const [slaList, setSlaList] = useState();
    const [reCalculate, setReCalculate] = useState(false);
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(true);
        const { promise, cancel } = GlobalSettingAPI.getFindingReopenState();
        promise
            .then((data) => setReCalculate(data.recalculateSlaWhenFindingReopens))
            .finally(() => setLoading(false));
        return cancel;
    }, []);

    const fetchSLA = useCallback(() => {
        setSlaList()
        const { promise, cancel } = GlobalSettingAPI.getAllSLA();
        promise.then(setSlaList)
        return cancel;
    }, [])


    useEffect(() => {
        fetchSLA();
    }, [globalFilterHash,fetchSLA]);



    const onAddSLA = useCallback((e) => {
        e.stopPropagation();
        ModalUtil.show({
            content: <SLAForm />,
            afterClose: fetchSLA
        })
    }, [fetchSLA]);

    const onAllowReCalculateChange = useCallback(() => {
        ModalUtil.show({
            content: <WarningPopup
                type=""
                title={<>Are you sure you want to Re-Calculate SLA When Finding Re-<Vocabulary name="open" isPlural={true}/>?</>}
                okText="Yes"
                onOk={async () => {
                    const messageReference = message.loading("Updating...");
                    setLoading(true);
                    await GlobalSettingAPI.setFindingReopenState({ recalculateSlaWhenFindingReopens: !reCalculate }).promise;
                    setLoading(false);
                    setReCalculate(!reCalculate);
                    messageReference();
                    message.success("Updated Successfully")
                }}
            />
        });
    },[reCalculate]);

    return <>
        <>
            <Spin spinning={_.isUndefined(slaList)}>
                <Title level={5}>Findings SLA Configuration</Title>
                <div>You can set <Vocabulary name="triage" isLowerCase={true}/> SLA and <Vocabulary name="resolution" isLowerCase={true}/> SLA here.</div>
                <>
                    <div className="d-flex align-items-center justify-content-between m-y-sm">
                    <span>Re-calculate SLA When Finding Re-<Vocabulary name="open" isPlural={true}/>: <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}><Switch 
                    loading={loading}
                    size="small" style={{ marginLeft: 5 }} checked={!!reCalculate} onChange={onAllowReCalculateChange} /></AuthorisedContainer></span>
                        <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}><Button size="small" type="primary" onClick={onAddSLA}>Add SLA</Button></AuthorisedContainer>
                    </div>
                    {!_.isEmpty(slaList) && ConfigItem(slaList, fetchSLA)}
                </>
            </Spin>
        </>
    </>
}

function ConfigItem(slaList, fetchSLA) {
    const onEditSLA = (e, data) => {
        e.stopPropagation();
        ModalUtil.show({
            content: <SLAForm data={data} slaId={data.id}/>,
            afterClose: fetchSLA
        })
    };

    const onDeleteSLA = (e) => {
        e.stopPropagation();

        const slaData = JSON.parse(e.currentTarget.dataset.sla);
        if(slaData.isGlobal){
            return;
        }
        ModalUtil.show({
            content: <DeletePopup  
                promptMessage={`You want to delete "${slaData.name}" SLA configuration?`}
                note={false}
                confirmText={"Delete"}
                onDelete={async () => {
                    await GlobalSettingAPI.deleteSLA(slaData.id).promise;
                    message.success('Successfully deleted configuration.');
                }}
            />,
            afterClose: fetchSLA
        });
    }

    const copySla = (e) => {
        e.stopPropagation();
        const slaData = JSON.parse(e.currentTarget.dataset.sla);
        ModalUtil.show({
            content: <SLAForm data={_.omit(slaData, ['id', 'name'])} />,
            afterClose: fetchSLA
        })
    };

    const getExtra = (data) => {
        const toolTipTitle = data.isGlobal ? "Global SLA can't be deleted" : "Delete";
        return (
            <>
                <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING} tooltip="Edit"><EditIcon onClick={(e) => onEditSLA(e, data)} /></AuthorisedContainer>
                <Divider type="vertical" className="m-x-xs" />
                <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING} tooltip={"Copy"} placement="left">
                    <CopyIcon data-sla={JSON.stringify(data)} onClick={copySla} />
                </AuthorisedContainer>    
                <Divider type="vertical" className="m-x-xs" />
                <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING} tooltip={toolTipTitle} placement="left">
                    <DeleteIcon className={classNames("danger", {"disabled": data.isGlobal})} data-sla={JSON.stringify(data)} onClick={onDeleteSLA} />
                </AuthorisedContainer>     
            </>
        )
    }

    const renderHeader = (data) => {
        const { product, subproduct } = data.configs || {};
        const { isGlobal, productCount, subProductCount } = data;
        const productList = (!_.isEmpty(product)) ? renderPopOverContent(product) : undefined;
        const subProductList = (!_.isEmpty(subproduct)) ? renderPopOverContent(subproduct) : undefined;

        let productLength = product?.length || 0;
        let subProductLength = subproduct?.length || 0;
        
        //if isGlobal is true, the SLA is Default SLA
        if(isGlobal){
            productLength = productCount || 0;
            subProductLength = subProductCount || 0;
        }

        const productDetail = <b><Vocabulary name="product" />: {productLength}</b>;
        const subProductDetail = <b><Vocabulary name="subProduct" />: {subProductLength}</b>;

        return (
            <>
                {data.name} {" "}
                ({productList && !isGlobal
                    ? 
                    <Popover content={productList} trigger={["hover"]}>
                        {productDetail}
                    </Popover>
                    :
                    productDetail
                }
                <Divider type="vertical" className="m-x-xs" />
                {subProductList && !isGlobal
                    ? 
                    <Popover content={subProductList} trigger={["hover"]}>
                        {subProductDetail}
                    </Popover>
                    :
                    subProductDetail
                })
            </>
        );
    }

    return (
        <Collapse
            destroyInactivePanel={true}
            className="pull-based-tool"
            defaultActiveKey={slaList[0].id}
            bordered={false}
            expandIconPosition="left"
            expandIcon={({ isActive }) => <Tooltip title={isActive ? "Close SLA" : "Open SLA"}><CaretDownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 20 }} /></Tooltip>}
        >
            {
                slaList.map((data) => (
                    <Collapse.Panel key={data.id} header={renderHeader(data)} extra={getExtra(data)}>
                        <ConfigCard data={data} />
                    </Collapse.Panel>
                ))
            }

        </Collapse>

    )
}

const hasSlaType = (sla, type) => _.isArray(sla) && sla.every((item) => item[type] != null);

export function ConfigCard({ data, hideDesc = false, viewOnly = false }) {
    const { description, sla, name } = data;

    return (
        <Row gutter={BASE_GUTTER}>
            { !hideDesc && (<>
                <Col span={24}><Text strong>Description</Text></Col>
                <Col span={24}>{description}</Col>
                </>)
            }
            {viewOnly && (<>
                <Col span={24}><b>SLA Name:</b> {name}</Col>
                </>)
            }
            {hasSlaType(sla, 'firstResponseSla') && <Col span={8}>
                <Row gutter={BASE_GUTTER}>
                    <Col span={24}>
                        <Text strong><Vocabulary name="triage"/> SLA</Text>
                    </Col>
                    <CardContent sla={sla} type="firstResponseSla" />
                </Row>
            </Col>}
            {hasSlaType(sla, 'developerSla') && <Col span={8}>
                <Row gutter={BASE_GUTTER}>
                    <Col span={24}>
                        <Text strong><Vocabulary name="remediation"/> SLA</Text>
                    </Col>
                    <CardContent sla={sla} type="developerSla" />
                </Row>
            </Col>}
            {hasSlaType(sla, 'resolutionSla') && <Col span={8}>
                <Row gutter={BASE_GUTTER}>
                    <Col span={24}>
                        <Text strong><Vocabulary name="resolution"/> SLA</Text>
                    </Col>
                    <CardContent sla={sla} type="resolutionSla" />
                </Row>
            </Col>}
        </Row>
    )
}
function CardContent({ sla, type }) {
    if(!_.isArray(sla)){
        return null;
    }
    return sla.map((slaContent, i) => <React.Fragment key={`${slaContent.id || (i+1)}_${type}`}>
        <Col span={14}>
            {slaContent.riskRating ? <ACRiskRatingTag rating={slaContent.riskRating} /> : <Text strong style={{ color: SEVERITY_COLOR_TEXT[slaContent.severity] }}>{getVocabulary(slaContent.severity)}</Text>} 
        </Col>
        <Col span={10}>
            <Text>{renderTime(slaContent[type])}  Day(s)</Text>
        </Col>
    </React.Fragment>)
}

export function renderTime(time, format = 'days') {
    switch (format) {
        case 'days':
            return moment.duration(time, 'milliseconds').asDays().toFixed();
        default:
            break;
    }
}